import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Clock, Mail, Globe, Plus, Play, Trash2, Settings, RefreshCw, CreditCard, Lock } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";

interface ScheduledScan {
  id: string;
  userId: string;
  url: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  enabled: boolean;
  emailRecipient: string;
  lastRun?: string;
  nextRun: string;
}

export default function ScheduledScans() {
  const { toast } = useToast();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newScan, setNewScan] = useState({
    url: '',
    frequency: 'daily' as const,
    emailRecipient: ''
  });

  // Get subscription status
  const { data: subscriptionStatus } = useQuery({
    queryKey: ["/api/subscription/status"],
  });

  const { data: scheduledScans, isLoading, refetch, error } = useQuery({
    queryKey: ["/api/scheduled-scans"],
    staleTime: 0, // Force fresh data
    cacheTime: 0, // Don't cache
    refetchInterval: 5000, // Poll every 5 seconds as fallback
    onSuccess: (data) => {
      console.log(`📊 Query success - received ${data?.length || 0} scheduled scans`);
      console.log(`📊 Query data:`, data);
    },
    onError: (error: any) => {
      console.error(`❌ Query error:`, error);
    },
    retry: false, // Don't retry on 403 errors
  });

  // Check if user is on trial and got blocked
  const isTrialBlocked = error && (error as any)?.status === 403 && (error as any)?.data?.isTrial;

  const scansArray = Array.isArray(scheduledScans) ? scheduledScans : [];
  
  // Debug logging
  console.log('🔍 Scheduled scans data:', scheduledScans);
  console.log('🔍 Scans array:', scansArray);

  const createScanMutation = useMutation({
    mutationFn: async (scanData: typeof newScan) => {
      console.log('🚀 Creating scheduled scan with data:', scanData);
      try {
        const result = await apiRequest("/api/scheduled-scans", { method: "POST", body: scanData });
        console.log('✅ Scheduled scan created successfully:', result);
        return result;
      } catch (error) {
        console.error('❌ Error creating scheduled scan:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-scans"] });
      setShowAddForm(false);
      setNewScan({ url: '', frequency: 'daily', emailRecipient: '' });
      toast({ title: "Scheduled scan created successfully" });
    },
    onError: (error: any) => {
      if (error?.status === 403 && error?.data?.isTrial) {
        toast({ 
          title: "Paid Feature", 
          description: "Scheduled scans are only available for paid subscribers. Please upgrade your plan.",
          variant: "destructive" 
        });
      } else {
        toast({ title: "Failed to create scheduled scan", variant: "destructive" });
      }
    },
  });

  const updateScanMutation = useMutation({
    mutationFn: async ({ scanId, updates }: { scanId: string; updates: Partial<ScheduledScan> }) => {
      return apiRequest(`/api/scheduled-scans/${scanId}`, { method: "PUT", body: updates });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-scans"] });
      toast({ title: "Scheduled scan updated" });
    },
    onError: () => {
      toast({ title: "Failed to update scheduled scan", variant: "destructive" });
    },
  });

  const deleteScanMutation = useMutation({
    mutationFn: async (scanId: string) => {
      console.log(`🗑️ Attempting to delete scheduled scan: ${scanId}`);
      return apiRequest(`/api/scheduled-scans/${scanId}`, { method: "DELETE" });
    },
    onSuccess: (data) => {
      console.log(`✅ Delete successful:`, data);
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-scans"] });
      toast({ title: "Scheduled scan removed" });
    },
    onError: (error) => {
      console.error(`❌ Delete failed:`, error);
      toast({ title: "Failed to remove scheduled scan", variant: "destructive" });
    },
  });

  const testScanMutation = useMutation({
    mutationFn: async ({ url, emailRecipient }: { url: string; emailRecipient: string }) => {
      return apiRequest("/api/test-scheduled-scan", { method: "POST", body: { url, emailRecipient } });
    },
    onSuccess: () => {
      toast({ title: "Test scan report sent to your email" });
    },
    onError: (error: any) => {
      const message = error?.message || "Failed to send test scan report - check Oracle OCI SMTP configuration in .env";
      toast({ title: message, variant: "destructive" });
    },
  });

  const handleCreateScan = () => {
    console.log('📝 Form submitted with data:', newScan);
    if (!newScan.url || !newScan.emailRecipient) {
      console.log('❌ Form validation failed - missing fields');
      toast({ title: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    console.log('✅ Form validation passed, calling mutation');
    createScanMutation.mutate(newScan);
  };

  const toggleScanEnabled = (scanId: string, enabled: boolean) => {
    updateScanMutation.mutate({ scanId, updates: { enabled } });
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'daily': return 'Daily';
      case 'weekly': return 'Weekly';
      case 'monthly': return 'Monthly';
      default: return frequency;
    }
  };

  const getNextRunTime = (nextRun: string) => {
    return new Date(nextRun).toLocaleDateString() + ' at ' + new Date(nextRun).toLocaleTimeString();
  };

  // Show upgrade prompt for trial users
  if (isTrialBlocked || (subscriptionStatus?.isTrial && !subscriptionStatus?.hasActiveSubscription)) {
    return (
      <div className="space-y-6 w-full max-w-none">
        <Card className="border-2 border-yellow-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-600">
              <Lock className="h-5 w-5" />
              Paid Feature - Upgrade Required
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-600 dark:text-slate-400">
              Scheduled Scans is a premium feature available only for paid subscribers. 
              Trial users can scan domains manually, but automated scheduled scans require a subscription.
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Upgrade to unlock:</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 dark:text-slate-400">
                <li>Automated scheduled scans (daily, weekly, monthly)</li>
                <li>Email reports delivered automatically</li>
                <li>Continuous security monitoring</li>
                <li>Priority support</li>
              </ul>
            </div>
            <Link href="/subscription">
              <Button className="w-full sm:w-auto">
                <CreditCard className="h-4 w-4 mr-2" />
                Upgrade to Paid Plan
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full max-w-none">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Scheduled Scans
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Automate your security scans with scheduled monitoring
          </p>
        </div>
      </div>

      <div className="space-y-6">
            {/* Add New Scheduled Scan */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center space-x-2">
                    <Plus className="w-5 h-5 text-primary-600" />
                    <span>Create Scheduled Scan</span>
                  </CardTitle>
                  <Button 
                    onClick={() => setShowAddForm(!showAddForm)}
                    variant={showAddForm ? "outline" : "default"}
                  >
                    {showAddForm ? "Cancel" : "Add New Scan"}
                  </Button>
                </div>
              </CardHeader>
              {showAddForm && (
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="url">Website URL</Label>
                      <Input
                        id="url"
                        placeholder="https://example.com"
                        value={newScan.url}
                        onChange={(e) => setNewScan({ ...newScan, url: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Recipient</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="admin@company.com"
                        value={newScan.emailRecipient}
                        onChange={(e) => setNewScan({ ...newScan, emailRecipient: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="frequency">Scan Frequency</Label>
                      <Select value={newScan.frequency} onValueChange={(value: any) => setNewScan({ ...newScan, frequency: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily (Recommended)</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      onClick={handleCreateScan} 
                      disabled={createScanMutation.isPending}
                      className="bg-primary-600 hover:bg-primary-700"
                    >
                      {createScanMutation.isPending ? "Creating..." : "Create Scheduled Scan"}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        if (newScan.url && newScan.emailRecipient) {
                          testScanMutation.mutate({ url: newScan.url, emailRecipient: newScan.emailRecipient });
                        }
                      }}
                      disabled={testScanMutation.isPending || !newScan.url || !newScan.emailRecipient}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {testScanMutation.isPending ? "Sending..." : "Test Email Report"}
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Existing Scheduled Scans */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-5 h-5 text-primary-600" />
                    <span>Active Scheduled Scans</span>
                  </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                console.log('🧪 Manual test - fetching scheduled scans...');
                try {
                  const response = await fetch('/api/scheduled-scans', { credentials: 'include' });
                  const data = await response.json();
                  console.log('🧪 Manual test result:', data);
                } catch (error) {
                  console.error('🧪 Manual test error:', error);
                }
              }}
            >
              Test GET
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                console.log('🧪 Manual test - creating scheduled scan...');
                try {
                  const response = await fetch('/api/scheduled-scans', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                      url: 'https://manual-test.com',
                      frequency: 'daily',
                      emailRecipient: 'test@example.com'
                    })
                  });
                  const data = await response.json();
                  console.log('🧪 Manual POST test result:', data);
                } catch (error) {
                  console.error('🧪 Manual POST test error:', error);
                }
              }}
            >
              Test POST
            </Button>
          </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg animate-pulse">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
                          <div>
                            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32 mb-2"></div>
                            <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-24"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : scansArray.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-600 dark:text-slate-400 mb-4">
                      No scheduled scans configured
                    </p>
                    <Button 
                      onClick={() => setShowAddForm(true)}
                      className="bg-primary-600 hover:bg-primary-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Scheduled Scan
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {scansArray.map((scan: ScheduledScan) => (
                      <div key={scan.id} className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                        <div className="flex items-center space-x-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            scan.enabled 
                              ? 'bg-green-100 dark:bg-green-900/30' 
                              : 'bg-gray-100 dark:bg-gray-900/30'
                          }`}>
                            <Globe className={`w-5 h-5 ${
                              scan.enabled 
                                ? 'text-purple-600 dark:text-purple-400' 
                                : 'text-gray-600 dark:text-gray-400'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {scan.url}
                            </p>
                            <div className="flex items-center space-x-4 text-sm text-slate-600 dark:text-slate-400">
                              <span className="flex items-center space-x-1">
                                <Clock className="w-3 h-3" />
                                <span>{getFrequencyLabel(scan.frequency)}</span>
                              </span>
                              <span className="flex items-center space-x-1">
                                <Mail className="w-3 h-3" />
                                <span>{scan.emailRecipient}</span>
                              </span>
                              {scan.nextRun && (
                                <span>Next: {getNextRunTime(scan.nextRun)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Badge variant={scan.enabled ? "default" : "secondary"}>
                            {scan.enabled ? "Active" : "Disabled"}
                          </Badge>
                          <Switch
                            checked={scan.enabled}
                            onCheckedChange={(enabled) => toggleScanEnabled(scan.id, enabled)}
                            disabled={updateScanMutation.isPending}
                          />
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => testScanMutation.mutate({ url: scan.url, emailRecipient: scan.emailRecipient })}
                            disabled={testScanMutation.isPending}
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => deleteScanMutation.mutate(scan.id)}
                            disabled={deleteScanMutation.isPending}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Email Configuration Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Settings className="w-5 h-5 text-primary-600" />
                  <span>Email Configuration</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                    Automated Security Reports
                  </h4>
                  <p className="text-blue-800 dark:text-blue-200 text-sm mb-3">
                    Scheduled scans will automatically detect JavaScript changes and send detailed email reports including:
                  </p>
                  <ul className="text-blue-800 dark:text-blue-200 text-sm space-y-1 list-disc list-inside">
                    <li>New JavaScript files detected on your website</li>
                    <li>Modified scripts with content hash changes</li>
                    <li>Security score changes and vulnerability assessments</li>
                    <li>Content Security Policy (CSP) analysis and recommendations</li>
                    <li>Suspicious script detection (eval usage, high entropy)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
      </div>
    </div>
  );
}